/**
 * What's On / Places data plumbing (web).
 *
 * One module, so there is exactly one place to change when Phase 4 lands.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY RAW `fetch` AND NOT THE GENERATED CLIENT
 *
 * `lib/api-spec/openapi.yaml` documents **events only** (audit §4.6, R1).
 * `/bars`, `/venues`, `/whats-on`, `/facility-definitions` and
 * `/venues/:id/{activities,facilities,sports}` have no spec coverage, so
 * `lib/api-client-react` generates no hooks for them and ~9 files re-declare
 * their response shapes by hand. This module is the web app's single
 * hand-declaration, replacing four of those nine.
 *
 * Even for events the generated hook is not enough: `GetEventsParams` covers
 * only `town|category|featured|businessName|limit|offset`, while the live
 * route also accepts `dateFrom|dateTo|upcoming|venueId|search|status`
 * (api-server/src/routes/events.ts:72) and the row it returns carries
 * `venueId`, `isVenueEvent`, `endTime`, `shortSummary` and `sponsored`, none
 * of which exist on the generated `Event` type.
 *
 * ACTION FOR PHASE 4 (MIGRATION_REPORT §11 item 9): add these paths to
 * openapi.yaml, regenerate, and delete the interfaces below.
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { amenitiesToFacilitySlugs } from "./venue-utils";

/* ── Shared fetch ─────────────────────────────────────────────────────── */

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw Object.assign(new Error(`GET ${url} failed`), { status: res.status });
  return (await res.json()) as T;
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/* ── Events ───────────────────────────────────────────────────────────── */

/**
 * A row from `GET /api/events`.
 *
 * NOTE: the list endpoint strips `slug` (`events.ts:159`
 * `({ slug: _slug, ...e })`), so list items are addressed by numeric id.
 * `GET /events/:id` resolves either, and the 22 indexed slug URLs keep
 * working — we simply cannot *link* by slug from a list response.
 */
export interface HubEvent {
  id: number;
  name: string;
  description: string | null;
  shortSummary?: string | null;
  category: string;
  town: string;
  venue?: string | null;
  venueId?: number | null;
  isVenueEvent?: boolean | null;
  address?: string | null;
  eventDate: string;
  eventTime?: string | null;
  endTime?: string | null;
  price?: string | null;
  imageUrl?: string | null;
  bookingLink?: string | null;
  ticketUrl?: string | null;
  organiserName?: string | null;
  featured?: boolean;
  sponsored?: boolean;
  isSaved?: boolean;
}

export interface UseEventsArgs {
  /** Comma-separated town list from `useTown().townParam`, or undefined for all. */
  town?: string;
  category?: string;
  /** Server-side date narrowing. Presets resolve client-side too, so this is optional. */
  dateFrom?: string;
  dateTo?: string;
  venueId?: number;
  limit?: number;
  enabled?: boolean;
}

/**
 * Upcoming approved events.
 *
 * The default limit on `GET /events` is 20; we ask for more because the whole
 * feed is bucketed **client-side** by date. That is deliberate: switching
 * "Tonight" → "This weekend" then costs nothing, and — more importantly — we
 * can show a truthful count on every preset chip *before* the user commits to
 * it, and offer "nothing tonight, but 3 things this weekend" when a bucket is
 * empty. Neither is possible if each preset is its own round trip.
 *
 * At current volume (22 rows) this is one small request. If the dataset grows
 * past ~200 upcoming events, move the presets back to `dateFrom`/`dateTo`
 * (already wired above) and drop the counts.
 */
export function useEvents(args: UseEventsArgs = {}) {
  const { town, category, dateFrom, dateTo, venueId, limit = 200, enabled = true } = args;
  const url = `/api/events${qs({
    town, category, dateFrom, dateTo, venueId, limit,
    upcoming: dateFrom || dateTo ? undefined : true,
  })}`;

  return useQuery({
    queryKey: ["hub-events", { town, category, dateFrom, dateTo, venueId, limit }],
    queryFn: ({ signal }) => getJson<{ items: HubEvent[]; total: number }>(url, signal),
    enabled,
    staleTime: 60_000,
  });
}

/* ── What's On (recurring venue activities) ───────────────────────────── */

export interface WhatsOnVenueRef {
  id: number;
  name: string;
  slug: string | null;
  town: string | null;
  venueType: string;
  coverPhoto: string | null;
  address: string | null;
}

export interface WhatsOnItem {
  id: string;
  type: "activity" | "event";
  title: string;
  description: string | null;
  category: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  time: string | null;
  priceText: string | null;
  isFree: boolean;
  isException: boolean;
  frequency: string;
  dayOfWeek: number | null;
  venue: WhatsOnVenueRef;
  /** Backward-compat alias the server still emits (venueActivities.ts:55). */
  bar?: WhatsOnVenueRef;

  /* ── Added by Phase 4. Optional so this file still compiles and behaves
        against an older API build (an in-flight deploy, or a cached SW). ── */
  /** Real `bar_listings.id`, or null when the location is free text. */
  venueId?: number | null;
  /** false when there is no venue record — a park, a market, a civic hall. */
  hasVenueProfile?: boolean;
  /** Display location: venue name, or the event's free-text venue/address/town. */
  locationName?: string;
  /** Source event id / slug, for deep-linking. Null on activities. */
  eventId?: number | null;
  eventSlug?: string | null;
}

/**
 * Recurring venue activities, expanded into dated occurrences by the server's
 * `expandOccurrences` engine (venueActivities.ts:113 — the best-engineered
 * part of the subsystem per the audit, §5.1).
 *
 * WE KEEP ONLY `type === "activity"`, and that is deliberate even though
 * Phase 4 has now widened `/whats-on` to return non-venue events too
 * (venueActivities.ts, "Phase 4 change"). Two reasons:
 *
 *   1. Duplication. Those same events already arrive via `useEvents()`.
 *      Taking events from `/events` and activities from `/whats-on` is the
 *      exact union with no overlap.
 *   2. Imagery. The `WhatsOnItem` shape carries no `imageUrl` or `featured`
 *      field — a `/whats-on`-only feed would be a wall of gradient
 *      placeholders. `/events` returns the real `image_url` (15 of 22 rows
 *      have one), and this is a discovery surface where photography earns
 *      its space.
 *
 * `venue_activities` currently holds 0 rows, so this returns [] in production
 * today. It is wired now so the first venue to complete onboarding appears
 * without a deploy.
 */
export function useVenueActivities(args: { town?: string; days?: number } = {}) {
  const { town, days = 90 } = args;
  const url = `/api/whats-on${qs({ town, days, limit: 200 })}`;

  const query = useQuery({
    queryKey: ["hub-whats-on", { town, days }],
    queryFn: ({ signal }) => getJson<{ items: WhatsOnItem[] }>(url, signal),
    staleTime: 60_000,
  });

  const activities = useMemo(
    () => (query.data?.items ?? []).filter((i) => i.type === "activity"),
    [query.data],
  );

  return { ...query, activities };
}

/* ── Venues ───────────────────────────────────────────────────────────── */

export interface HubVenue {
  id: number;
  name: string;
  slug: string | null;
  venueType: string;
  description: string | null;
  town: string | null;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  coverPhoto: string | null;
  photos: string[] | null;
  /** Legacy flat array. See venue-utils.LEGACY_AMENITY_TO_FACILITY. */
  amenities: string[] | null;
  openingHours: Record<string, { open: string; close: string; closed: boolean }> | null;
  happyHourText: string | null;
  ageRestriction: string | null;
  accessibilityInfo: string | null;
  foodInfo: string | null;
  isFeatured: boolean;
  businessId: number | null;
  specials?: Array<{ id: number; title: string; dayOfWeek: number | null; frequency: string }>;
}

/**
 * Active venues.
 *
 * Uses `/api/bars` rather than `/api/venues` for one reason: it is the only
 * one of the two that returns `specials` alongside the row, and it is what
 * the previous Venues page already called.
 *
 * ⚠ PREVIOUS BUG, FIXED HERE: `/api/bars` returns `{ items, total }`
 * (bars.ts:72) but the old `Venues.tsx` typed the response as `Venue[]` and
 * called `.map` on it directly — which throws on a real response. The page
 * only ever rendered its loading and error states.
 *
 * `facility` is a real server-side filter as of Phase 4
 * (api-server/src/lib/facilityFilter.ts + facilityFilterSql.ts). Semantics,
 * verified against that implementation rather than assumed:
 *
 *   • comma-separated OR repeated: `?facility=pool-table,beer-garden`
 *   • **AND**, not OR — a venue must have every requested facility
 *   • accepts canonical slugs (`pool-table`) and legacy underscore tokens
 *     (`pool_table`), so either vocabulary works
 *   • absent param = no filter (existing clients unaffected)
 *   • capped at MAX_FACILITY_FILTERS = 10 distinct slugs per request
 *   • matches if EITHER `venue_facilities` OR `bar_listings.amenities` says so
 *
 * Places.tsx therefore sends one comma-joined `facility` value and does no
 * client-side facility narrowing of its own.
 */
export function useVenues(args: { town?: string; type?: string; facility?: string } = {}) {
  const { town, type, facility } = args;
  const url = `/api/bars${qs({ town, type, facility, limit: 50 })}`;

  return useQuery({
    queryKey: ["hub-venues", { town, type, facility }],
    queryFn: ({ signal }) => getJson<{ items: HubVenue[]; total: number }>(url, signal),
    staleTime: 5 * 60_000,
  });
}

export function useVenue(identifier: string | undefined) {
  const isNumeric = !!identifier && /^\d+$/.test(identifier);
  const url = isNumeric
    ? `/api/venues/${identifier}`
    : `/api/venues/by-slug/${encodeURIComponent(identifier ?? "")}`;

  return useQuery({
    queryKey: ["hub-venue", identifier],
    queryFn: ({ signal }) => getJson<HubVenue>(url, signal),
    enabled: !!identifier,
    staleTime: 5 * 60_000,
  });
}

/* ── Facilities ───────────────────────────────────────────────────────── */

export interface FacilityDefinition {
  id: number;
  slug: string;
  name: string;
  /** kebab-case lucide icon NAME, not an emoji. See venue-utils.facilityIcon. */
  icon: string;
  description: string | null;
  category: string;
  sortOrder: number;
  isActive: boolean;
}

export interface VenueFacility {
  id: number;
  venueId: number;
  facilityDefinitionId: number;
  notes: string | null;
  quantity: number | null;
  priceText: string | null;
  bookingRequired: boolean | null;
  isActive: boolean;
  definition: { name: string; icon: string; category: string; slug: string };
}

/** The controlled vocabulary — 29 rows, admin-editable, already seeded. */
export function useFacilityDefinitions() {
  return useQuery({
    queryKey: ["hub-facility-definitions"],
    queryFn: ({ signal }) => getJson<{ items: FacilityDefinition[] }>("/api/facility-definitions", signal),
    staleTime: 30 * 60_000,
  });
}

export function useVenueFacilities(venueId: number | undefined) {
  return useQuery({
    queryKey: ["hub-venue-facilities", venueId],
    queryFn: ({ signal }) => getJson<{ items: VenueFacility[] }>(`/api/venues/${venueId}/facilities`, signal),
    enabled: !!venueId && venueId > 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Facility slugs for many venues at once — for DISPLAY (the icon row on each
 * card), not for filtering. Filtering is the server's job via `?facility=`.
 *
 * ⚠ STUB — this is an N+1, and it is the one thing on these pages still
 * waiting on the API.
 *
 * `GET /venues/:id/facilities` (facilities.ts:134) remains the only way to
 * READ `venue_facilities`; Phase 4 added cross-venue *filtering* but not
 * cross-venue *reading*, so there is still no way to ask for the facilities of
 * 20 venues in one call. With 3 active venues that is 3 cached parallel
 * requests. It will not be acceptable at 200.
 *
 * WHAT WOULD FIX IT: have `GET /bars` / `GET /venues` embed a
 * `facilitySlugs: string[]` (or the full facility rows) on each item, exactly
 * as `GET /bars` already embeds `specials`. Then delete this hook.
 *
 * Until then the result is the UNION of:
 *   • structured `venue_facilities` rows (authoritative, may carry notes), and
 *   • `bar_listings.amenities` mapped through the Phase 3 alias map,
 * which mirrors the server's own matching rule in facilityFilter.ts, so the
 * icons on a card can never disagree with the filter that selected it.
 */
export function useVenueFacilityMap(venues: readonly HubVenue[]) {
  const ids = useMemo(() => venues.map((v) => v.id), [venues]);

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["hub-venue-facilities", id],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getJson<{ items: VenueFacility[] }>(`/api/venues/${id}/facilities`, signal),
      staleTime: 5 * 60_000,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);

  const map = useMemo(() => {
    const out = new Map<number, Set<string>>();
    venues.forEach((venue, i) => {
      // Legacy array first so a structured row can only ever add, never remove.
      const slugs = new Set<string>(amenitiesToFacilitySlugs(venue.amenities));
      const rows = results[i]?.data?.items ?? [];
      for (const row of rows) {
        if (row.isActive !== false) slugs.add(row.definition.slug);
      }
      out.set(venue.id, slugs);
    });
    return out;
    // `results` is a new array identity every render; key off the settled data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, results.map((r) => r.dataUpdatedAt).join(",")]);

  return { facilityMap: map, isLoading };
}

/* ── Venue sub-resources (detail page) ────────────────────────────────── */

export interface VenueActivityRow {
  id: number;
  title: string;
  description: string | null;
  category: string | null;
  frequency: string;
  dayOfWeek: number | null;
  weekOfMonth: number | null;
  startTime: string | null;
  endTime: string | null;
  priceText: string | null;
  isFree: boolean;
  bookingRequired: boolean;
  bookingLink: string | null;
  status: string;
}

export function useVenueActivityList(venueId: number | undefined) {
  return useQuery({
    queryKey: ["hub-venue-activities", venueId],
    queryFn: ({ signal }) => getJson<{ items: VenueActivityRow[] }>(`/api/venues/${venueId}/activities`, signal),
    enabled: !!venueId && venueId > 0,
    staleTime: 60_000,
  });
}

export interface SportListingRow {
  id: number;
  sport: string;
  fixtureName: string | null;
  startDate: string;
  startTime: string | null;
  endTime: string | null;
}

export function useVenueSports(venueId: number | undefined) {
  return useQuery({
    queryKey: ["hub-venue-sports", venueId],
    queryFn: ({ signal }) => getJson<{ items: SportListingRow[] }>(`/api/venues/${venueId}/sports`, signal),
    enabled: !!venueId && venueId > 0,
    staleTime: 60_000,
  });
}
